<template>
  <div class="import-details-tree">
    <v-treeview
      v-if="detail_tree_items.length > 0"
      :items="detail_tree_items"
      item-key="id"
      item-text="name"
      :open.sync="details_tree_open"
      open-on-click
      dense
      class="details-tree"
    >
      <template v-slot:label="{ item }">
        <div class="details-tree-item">
          <span class="details-tree-key">{{ item.name }}</span>
          <span v-if="item.children" class="details-tree-count">
            {{ item.value }}
          </span>
          <span v-else-if="item.value !== null && item.value !== undefined && item.value !== ''" class="details-tree-value">
            {{ item.value }}
          </span>
        </div>
      </template>
    </v-treeview>
    <div v-else class="text-center grey--text pa-4">
      {{ empty_text }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'import-details-tree',
  props: {
    value: { type: [Object, Array, String], default: null },
    open_depth: { type: Number, default: 2 },
    empty_text: { type: String, default: 'No details available' }
  },
  computed: {
    parsed_value() {
      if (this.value === null || this.value === undefined || this.value === '') {
        return null;
      }
      if (typeof this.value === 'string') {
        try {
          return JSON.parse(this.value);
        } catch (e) {
          return { Raw: this.value };
        }
      }
      return this.value;
    },
    detail_tree_items() {
      return this.buildDetailTreeItems(this.parsed_value);
    }
  },
  watch: {
    detail_tree_items: {
      immediate: true,
      handler(items) {
        this.details_tree_open = this.buildDetailOpenNodes(items, this.open_depth);
      }
    }
  },
  methods: {
    formatDetailKey(key) {
      return String(key).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    buildDetailTreeItems(details) {
      if (!details || typeof details !== 'object') {
        return [];
      }
      return this.buildDetailTreeNodes(details, []);
    },
    buildDetailTreeNodes(value, path) {
      if (Array.isArray(value)) {
        return value.map((entry, index) => {
          return this.buildDetailTreeNode(`Item ${index + 1}`, entry, path.concat(`item-${index + 1}`));
        });
      }
      if (value && typeof value === 'object') {
        return Object.keys(value).map((key) => {
          return this.buildDetailTreeNode(this.formatDetailKey(key), value[key], path.concat(key));
        });
      }
      return [];
    },
    buildDetailTreeNode(label, value, path) {
      const node = {
        id: this.getDetailNodeId(path),
        name: label
      };
      if (value && typeof value === 'object') {
        const children = this.buildDetailTreeNodes(value, path);
        node.children = children;
        node.value = this.getDetailCollectionSummary(value);
        return node;
      }
      node.value = this.formatDetailLeaf(value);
      return node;
    },
    getDetailNodeId(path) {
      if (!path || path.length === 0) {
        return 'details';
      }
      return path.join('/');
    },
    getDetailCollectionSummary(value) {
      if (Array.isArray(value)) {
        const count = value.length;
        if (count === 0) return '-';
        return `${count} ${count === 1 ? 'item' : 'items'}`;
      }
      if (value && typeof value === 'object') {
        const count = Object.keys(value).length;
        if (count === 0) return '-';
        return `${count} ${count === 1 ? 'field' : 'fields'}`;
      }
      return '';
    },
    buildDetailOpenNodes(items, max_depth, depth = 0) {
      if (!items || items.length === 0 || depth >= max_depth) {
        return [];
      }
      let open_nodes = [];
      items.forEach((item) => {
        if (item.children && item.children.length > 0) {
          open_nodes.push(item.id);
          open_nodes = open_nodes.concat(this.buildDetailOpenNodes(item.children, max_depth, depth + 1));
        }
      });
      return open_nodes;
    },
    formatDetailLeaf(value) {
      if (value === null || value === undefined || value === '') return '-';
      if (typeof value === 'boolean') return value ? 'true' : 'false';
      return value;
    },
    expandAll() {
      const all_node_ids = this.getAllNodeIds(this.detail_tree_items);
      this.details_tree_open = all_node_ids;
    },
    collapseAll() {
      this.details_tree_open = [];
    },
    getAllNodeIds(items) {
      let ids = [];
      if (!items) return ids;
      items.forEach((item) => {
        if (item.children && item.children.length > 0) {
          ids.push(item.id);
          ids = ids.concat(this.getAllNodeIds(item.children));
        }
      });
      return ids;
    }
  },
  data() {
    return {
      details_tree_open: []
    };
  }
};
</script>

<style scoped>
.import-details-tree {
  padding: 8px 0;
}

.details-tree {
  padding: 0 8px;
}

/* Remove pointer cursor from treeview nodes */
.details-tree ::v-deep .v-treeview-node__root {
  cursor: default;
}

/* Only show pointer on the expand/collapse toggle button */
.details-tree ::v-deep .v-treeview-node__toggle {
  cursor: pointer;
}

.details-tree-item {
  display: flex;
  gap: 8px;
  align-items: baseline;
  padding: 2px 0;
}

.details-tree-key {
  font-weight: 600;
  color: #1a1a1a;
}

.details-tree-value {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.85rem;
  font-family: 'Roboto Mono', 'Consolas', 'Monaco', monospace;
  background-color: rgba(0, 0, 0, 0.04);
  padding: 1px 6px;
  border-radius: 3px;
}

.details-tree-count {
  font-size: 0.75rem;
  color: #666;
  background-color: #e3f2fd;
  padding: 1px 8px;
  border-radius: 10px;
  font-weight: 500;
}
</style>
